import { useEffect, useState } from 'react';
import { adminService } from '../services/admin-service';
import { Loader2, TrendingUp, Users, MessageSquare, DollarSign } from 'lucide-react';

interface AIMetric {
  providerId: string;
  name: string;
  messages: number;
  activeUsers: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cost: number;
  enabled: boolean;
}

export function AdminAIMetrics() {
  const [aiMetrics, setAiMetrics] = useState<AIMetric[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  useEffect(() => {
    loadAIMetrics(selectedPeriod);
  }, [selectedPeriod]);

  const loadAIMetrics = async (days: number) => {
    try {
      setIsLoading(true);
      const data = await adminService.getAIMetrics(days);
      setAiMetrics(data);
    } catch (error) {
      console.error('Erro ao carregar métricas de IA:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const periodOptions = [
    { value: 7, label: 'Últimos 7 dias' },
    { value: 30, label: 'Últimos 30 dias' },
    { value: 60, label: 'Últimos 60 dias' },
    { value: 90, label: 'Últimos 90 dias' }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Carregando métricas das IAs...</span>
      </div>
    );
  }

  const totalCost = aiMetrics.reduce((sum, ai) => sum + ai.cost, 0);
  const totalMessages = aiMetrics.reduce((sum, ai) => sum + ai.messages, 0);
  const totalUsers = Math.max(...aiMetrics.map(ai => ai.activeUsers));

  const getAIIcon = (providerId: string) => {
    switch (providerId) {
      case 'openai':
        return '🤖';
      case 'anthropic':
        return '👤';
      case 'google':
        return '🔍';
      default:
        return '🤖';
    }
  };

  const getAIColor = (providerId: string) => {
    switch (providerId) {
      case 'openai':
        return 'blue';
      case 'anthropic':
        return 'purple';
      case 'google':
        return 'green';
      default:
        return 'gray';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Métricas por IA</h2>
        <div className="flex items-center gap-3">
          {/* Filtro de Período */}
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <button
            onClick={() => loadAIMetrics(selectedPeriod)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Atualizar Dados
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Custo Total</p>
              <p className="text-3xl font-bold text-red-600">{formatCurrency(totalCost)}</p>
              <p className="text-xs text-gray-500 mt-1">
                últimos {selectedPeriod} dia{selectedPeriod !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total de Mensagens</p>
              <p className="text-3xl font-bold text-blue-600">{formatNumber(totalMessages)}</p>
              <p className="text-xs text-gray-500 mt-1">todas as IAs</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <MessageSquare className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Usuários Ativos</p>
              <p className="text-3xl font-bold text-green-600">{formatNumber(totalUsers)}</p>
              <p className="text-xs text-gray-500 mt-1">no período</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Cards de IAs */}
      <div className="space-y-4">
        {aiMetrics
          .sort((a, b) => b.messages - a.messages)
          .map((ai) => {
            const color = getAIColor(ai.providerId);
            const bgColors = {
              blue: 'bg-blue-50',
              purple: 'bg-purple-50',
              green: 'bg-green-50',
              gray: 'bg-gray-50'
            };
            const borderColors = {
              blue: 'border-blue-200',
              purple: 'border-purple-200',
              green: 'border-green-200',
              gray: 'border-gray-200'
            };
            const textColors = {
              blue: 'text-blue-600',
              purple: 'text-purple-600',
              green: 'text-green-600',
              gray: 'text-gray-600'
            };

            return (
              <div
                key={ai.providerId}
                className={`bg-white rounded-lg shadow border-2 ${borderColors[color]} p-6`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 ${bgColors[color]} rounded-lg`}>
                      <span className="text-2xl">{getAIIcon(ai.providerId)}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{ai.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {ai.enabled ? (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                            Ativo
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                            Sem uso
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-500">Custo Total</p>
                    <p className={`text-2xl font-bold ${textColors[color]}`}>
                      {formatCurrency(ai.cost)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Mensagens</p>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(ai.messages)}</p>
                    <p className="text-xs text-gray-500">total enviadas</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">Usuários Ativos</p>
                    <p className={`text-2xl font-bold ${textColors[color]}`}>
                      {formatNumber(ai.activeUsers)}
                    </p>
                    <p className="text-xs text-gray-500">usuários únicos</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">Tokens Input</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(ai.inputTokens)}
                    </p>
                    <p className="text-xs text-gray-500">tokens enviados</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">Tokens Output</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(ai.outputTokens)}
                    </p>
                    <p className="text-xs text-gray-500">tokens recebidos</p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      <strong>Total de Tokens:</strong> {formatNumber(ai.totalTokens)}
                    </span>
                    <span className="text-gray-600">
                      <strong>Custo por Mensagem:</strong>{' '}
                      {ai.messages > 0 ? formatCurrency(ai.cost / ai.messages) : '$0.00'}
                    </span>
                    <span className="text-gray-600">
                      <strong>Média por Usuário:</strong>{' '}
                      {ai.activeUsers > 0
                        ? formatNumber(Math.round(ai.messages / ai.activeUsers))
                        : '0'}{' '}
                      msgs
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Informação sobre custos */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-yellow-100 rounded-lg">
            <TrendingUp className="w-5 h-5 text-yellow-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-yellow-900 mb-1">Sobre os Custos</h4>
            <p className="text-sm text-yellow-800">
              Os custos são estimados com base no número de tokens processados. A contagem é
              aproximada (≈4 caracteres por token). Custos reais podem variar conforme o modelo
              específico usado e promoções da API.
            </p>
            <div className="mt-2 text-xs text-yellow-700">
              <strong>Preços por 1M tokens:</strong>
              <ul className="ml-4 mt-1 space-y-1">
                <li>• OpenAI (GPT-4o): $5 input / $15 output</li>
                <li>• Anthropic (Claude 3.5): $3 input / $15 output</li>
                <li>• Google (Gemini 1.5): $1.25 input / $5 output</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

